import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  MdPerson,
  MdEmail,
  MdPhone,
  MdVerified,
  MdPending,
  MdEvent,
  MdLibraryMusic,
  MdDateRange,
  MdFlag,
  MdCheckCircle,
  MdAccessTime,
  MdDelete,
  MdWarning
} from 'react-icons/md';
import { getActivityDetails } from '../../api/activities';
import { getRegistrationsByActivity, deleteRegistration } from '../../api/registration';
import Notification from '../../components/Notification';
import DataTable from '../../components/DataTable';
import { formatWIBDateTime, formatWIBDate } from '../../utils/dateUtils';
import { formatCityWithCountry } from '../../utils/cityMappingUtils';
import { getFanMemberByActivityId } from '../../config/fanMembersConfig';

// Theme configuration
const THEMES = {
  event: {
    name: 'Event Management',
    primary: 'blue',
    gradient: 'from-blue-600 to-indigo-600',
    background: 'bg-gray-50',
    card: 'bg-white',
    text: 'text-gray-900',
    accent: 'text-blue-600',
    icon: MdEvent
  },
  fan: {
    name: 'Fan Management',
    primary: 'purple',
    gradient: 'from-purple-600 to-pink-600',
    background: 'bg-slate-50',
    card: 'bg-white',
    text: 'text-slate-900',
    accent: 'text-purple-600',
    icon: MdLibraryMusic
  }
};

const AdminHomePage = () => {
  const { activityId } = useParams();
  const [activityData, setActivityData] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('all');
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ isOpen: false, registration: null, loading: false });
  const itemsPerPage = 10;

  // Determine theme based on activity type
  const theme = THEMES[activityData?.type] || THEMES.event;

  const activeFanMember = useMemo(
    () => (activityData ? getFanMemberByActivityId(activityData.id) : null),
    [activityData],
  );
  const ThemeIcon = theme.icon;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Reset filters when activity changes
        setCityFilter('all');
        setSearchTerm('');
        
        const [activityResponse, registrationsResponse] = await Promise.all([
          getActivityDetails(activityId),
          getRegistrationsByActivity(activityId)
        ]);

        if (activityResponse?.success && activityResponse?.data) {
          setActivityData(activityResponse.data);
        }

        if (registrationsResponse?.success && registrationsResponse?.data) {
          setRegistrations(registrationsResponse.data);
        } else if (registrationsResponse?.data) {
          setRegistrations(Array.isArray(registrationsResponse.data) ? registrationsResponse.data : []);
        }
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.response?.data?.error || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activityId]);

  // Handle delete registration
  const handleDeleteRegistration = (registration) => {
    setDeleteConfirmModal({ isOpen: true, registration, loading: false });
  };

  const confirmDeleteRegistration = async () => {
    if (!deleteConfirmModal.registration) return;

    setDeleteConfirmModal(prev => ({ ...prev, loading: true }));

    try {
      await deleteRegistration(deleteConfirmModal.registration.id);
      
      // Remove from local state
      setRegistrations(prev => prev.filter(reg => reg.id !== deleteConfirmModal.registration.id));
      
      // Close modal
      setDeleteConfirmModal({ isOpen: false, registration: null, loading: false });
      
      // Show success notification
      setError(null); // Clear any previous errors
      // We could add a success notification here if we have a success state
      
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.response?.data?.error || 'Failed to delete registration');
      setDeleteConfirmModal({ isOpen: false, registration: null, loading: false });
    }
  };

  const cancelDeleteRegistration = () => {
    setDeleteConfirmModal({ isOpen: false, registration: null, loading: false });
  };

  // Get unique cities from registrations
  const uniqueCities = useMemo(() => {
    const cities = registrations
      .map(reg => reg?.city)
      .filter(city => city && city !== 'N/A' && city.trim() !== '')
      .filter((city, index, self) => self.indexOf(city) === index)
      .sort();
    return cities;
  }, [registrations]);

  // Filter registrations by city
  const filteredRegistrations = useMemo(() => {
    if (cityFilter === 'all') return registrations;
    return registrations.filter(reg => reg?.city === cityFilter);
  }, [registrations, cityFilter]);

  // Statistics calculations (based on filtered registrations)
  const verifiedCount = filteredRegistrations.filter(reg => reg?.isVerified).length;
  const unverifiedCount = filteredRegistrations.length - verifiedCount;
  const totalRegistrations = filteredRegistrations.length;

  // Define base table columns
  const baseColumns = [
    {
      header: 'User',
      accessor: 'user.name',
      searchFields: ['user.name', 'name', 'memberId'],
      render: (registration) => (
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${theme.gradient} flex items-center justify-center text-white font-semibold`}>
            {(registration?.user?.name || registration?.name)?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div>
            <div className="font-medium text-gray-900">
              {registration?.user?.name || registration?.name || 'Unknown User'}
            </div>
            <div className="text-sm text-gray-500">
              {activeFanMember
                ? `Member ID: ${registration?.memberId || 'N/A'}`
                : `ID: ${registration?.id || 'N/A'}`}
            </div>
          </div>
        </div>
      )
    },
    {
      header: 'Contact',
      accessor: 'user.email',
      searchFields: ['user.email', 'user.phoneNumber', 'email', 'phoneNumber'],
      render: (registration) => (
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <MdEmail className="text-gray-400 text-sm" />
            <span className="text-sm text-gray-900">
              {registration?.user?.email || registration?.email || 'N/A'}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <MdPhone className="text-gray-400 text-sm" />
            <span className="text-sm text-gray-600">
              {registration?.user?.phoneNumber || registration?.phoneNumber || 'N/A'}
            </span>
          </div>
        </div>
      )
    },
    {
      header: 'Registration Date',
      accessor: 'createdAt',
      render: (registration) => {
        const { dateStr, timeStr } = formatWIBDateTime(registration?.createdAt);

        return (
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900">{dateStr}</div>
            <div className="text-xs text-gray-500">{timeStr}</div>
          </div>
        );
      }
    },
    {
      header: 'Status',
      accessor: 'isVerified',
      render: (registration) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${registration?.isVerified
          ? 'bg-green-100 text-green-800'
          : 'bg-yellow-100 text-yellow-800'
          }`}>
          {registration?.isVerified ? (
            <>
              <MdVerified className="mr-1" />
              Verified
            </>
          ) : (
            <>
              <MdPending className="mr-1" />
              Pending
            </>
          )}
        </span>
      )
    },
    {
      header: 'Actions',
      accessor: 'actions',
      render: (registration) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleDeleteRegistration(registration)}
            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-full transition-colors duration-200"
            title="Delete registration"
          >
            <MdDelete className="text-lg" />
          </button>
        </div>
      )
    }
  ];

  // Fan-member activities include a Kota column in registration forms
  const columns = activeFanMember
    ? [
        ...baseColumns.slice(0, 3), // User, Contact, Registration Date
        {
          header: 'City',
          accessor: 'city',
          render: (registration) => (
            <div className="flex items-center space-x-2">
              <MdFlag className="text-gray-400 text-sm" />
            <span className="text-sm text-gray-900">
              {formatCityWithCountry(registration?.city)}
            </span>
            </div>
          )
        },
        ...baseColumns.slice(3) // Status
      ]
    : baseColumns;

  // Statistics cards configuration
  const statsCards = [
    {
      title: 'Total Registrations',
      value: totalRegistrations,
      icon: MdPerson,
      color: 'blue',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      borderColor: 'border-blue-200'
    },
    {
      title: 'Verified',
      value: verifiedCount,
      icon: MdCheckCircle,
      color: 'green',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      borderColor: 'border-green-200'
    },
    {
      title: 'Pending',
      value: unverifiedCount,
      icon: MdAccessTime,
      color: 'yellow',
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-600',
      borderColor: 'border-yellow-200'
    },
    {
      title: 'Verification Rate',
      value: totalRegistrations > 0 ? `${Math.round((verifiedCount / totalRegistrations) * 100)}%` : '0%',
      icon: MdVerified,
      color: 'purple',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      borderColor: 'border-purple-200'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {error && (
        <Notification
          type="error"
          message={error}
          onClose={() => setError(null)}
        />
      )}

      <div className="space-y-6 pb-6">
        {/* Header Section */}
        {activityData && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-4 mb-6">
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${theme.gradient} flex items-center justify-center`}>
                <ThemeIcon className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {activityData?.name || 'Activity Dashboard'}
                </h1>
                <p className="text-gray-600">{theme.name} Dashboard</p>
              </div>
            </div>

            {/* Activity Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                <MdDateRange className="text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Start Date</p>
                  <p className="text-gray-900">{formatWIBDate(activityData?.startAt)}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                <MdFlag className="text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-600">End Date</p>
                  <p className="text-gray-900">{formatWIBDate(activityData?.endAt)}</p>
                </div>
              </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {statsCards.map((stat, index) => {
                const IconComponent = stat.icon;
                return (
                  <div
                    key={index}
                    className={`${stat.bgColor} ${stat.borderColor} border rounded-lg p-4 hover:shadow-md transition-shadow duration-200`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                        <p className={`text-2xl font-bold ${stat.textColor}`}>{stat.value}</p>
                      </div>
                      <IconComponent className={`text-2xl ${stat.textColor}`} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Registrations Table Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Registration Management</h2>
                <p className="text-gray-600 mt-1">
                  Manage and monitor activity registrations
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <input
                  type="text"
                  placeholder="Search registrations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {(activeFanMember || uniqueCities.length > 0) && (
                  <select
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Cities</option>
                    {uniqueCities.map((city) => (
                      <option key={city} value={city}>
                        {formatCityWithCountry(city)}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto p-4">
            <DataTable
              data={filteredRegistrations}
              columns={columns}
              searchTerm={searchTerm}
              itemsPerPage={itemsPerPage}
              theme={theme}
              showItemsPerPageSelector={true}
            />
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirmModal.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-red-100">
                  <MdWarning className="text-red-600 text-xl" />
                </div>
              </div>
              
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Delete Registration
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Are you sure you want to delete the registration for{' '}
                  <span className="font-medium text-gray-900">
                    {deleteConfirmModal.registration?.user?.name || deleteConfirmModal.registration?.name || 'Unknown User'}
                  </span>? This action cannot be undone.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={cancelDeleteRegistration}
                    disabled={deleteConfirmModal.loading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md border border-gray-300 transition-colors duration-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteRegistration}
                    disabled={deleteConfirmModal.loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {deleteConfirmModal.loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Deleting...
                      </>
                    ) : (
                      'Delete Registration'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminHomePage;